import { TableSkeleton } from '@components/ui/skeleton/table';
import { DonationType } from '@givz/core-client';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { useSearchDonations } from '@queries/donations';
import { formatUsd } from '@utils/money';
import { FunctionComponent } from 'react';

export type DonationsProps = {
  incentiveGids?: string[];
};

export const Donations: FunctionComponent<DonationsProps> = (props) => {
  const { incentiveGids } = props;
  const searchParams = {
    campaignGids: incentiveGids,
    type: DonationType.Claimed,
  };
  const [{ data, isLoading }, searchDonation] =
    useSearchDonations(searchParams);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    searchDonation({
      ...searchParams,
      page: newPage + 1,
      pageSize: data?.data?.meta.itemsPerPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    searchDonation({
      ...searchParams,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  if (isLoading) {
    return <TableSkeleton rows={5} />;
  }

  return (
    <TableContainer>
      <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell align="right">Date</TableCell>
            <TableCell align="right">Charity</TableCell>
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.data?.donations.map((donation) => (
            <TableRow
              key={donation.gid}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {donation.gid}
              </TableCell>
              <TableCell align="right">
                {new Date(donation.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell align="right">{donation.organization.name}</TableCell>
              <TableCell align="right">
                {formatUsd(donation.amountPledged)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              count={data?.data?.meta.totalItems ?? 10}
              rowsPerPage={data?.data?.meta.itemsPerPage ?? 10}
              page={(data?.data?.meta.currentPage ?? 0) - 1}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};
